import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Select, MenuItem, FormControl } from '@mui/material';
import BaseField from './BaseField';
import { getDefaultOptions, formatFieldOptions } from '../../utils/fieldOptions';

/**
 * SelectField component for workflow forms.
 * Extends the BaseField with dropdown selection functionality.
 * Now with enhanced option handling for common fields like gender.
 */
const SelectField = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  required,
  error,
  helperText,
  disabled,
  fullWidth,
  className,
  placeholder,
  multiple,
  ...rest
}) => {
  // State to store processed options
  const [processedOptions, setProcessedOptions] = useState([]);

  // Process options on component mount or when options change
  useEffect(() => {
    // Get default options if none provided or if it's a known field type
    let fieldOptions = options && options.length > 0 
      ? options 
      : getDefaultOptions(name, 'select');
    
    // Format options to ensure they're in the correct format
    const formattedOptions = formatFieldOptions(fieldOptions);
    
    // Handle empty options for fields like gender by providing defaults
    if (formattedOptions.length === 0 && name.toLowerCase().includes('gender')) {
      setProcessedOptions(getDefaultOptions('gender'));
    } else {
      setProcessedOptions(formattedOptions);
    }
  }, [options, name]);

  return (
    <BaseField
      id={id}
      name={name}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      fullWidth={fullWidth}
      className={className}
    >
      <FormControl
        fullWidth={fullWidth}
        variant="outlined"
        error={Boolean(error)}
        size="small"
      >
        <Select
          id={id}
          name={name}
          value={value || (multiple ? [] : '')}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          displayEmpty={Boolean(placeholder)}
          multiple={multiple}
          {...rest}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {processedOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </BaseField>
  );
};

SelectField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      })
    ])
  ),
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
};

SelectField.defaultProps = {
  required: false,
  disabled: false,
  fullWidth: true,
  options: [],
  multiple: false,
};

export default SelectField;
